import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Spinner,
  Button,
  Container,
  Collapse,
  Alert,
  Modal,
} from "react-bootstrap";
import {
  getInvoiceById,
  createClaim,
  getClaimByInvoiceId,
  updateClaim,
  updateInvoice,
  sendInvoiceByEmail,
  getBankingDetailsByPracticeNumbers,
  decryptField,
} from "../../afinanceapi";
import { useParams } from "react-router-dom";
import {
  getAccountById,
  getPatientById,
  getPracticeById,
  getUserById,
} from "../../api";
import { SubmitClaim } from "./SubmitClaim";
import { ReverseClaim } from "./ReverseClaim";
import { useNavigate } from "react-router-dom";
import { ExportPDFInvoice } from "./ExportInvoicePDF";
import CreateReceiptModal from "./Receipt";
import { jwtDecode } from "jwt-decode";
import EmailInvoiceModal from "./EmailInvoice";
import { buildEmailData } from "./BuildInvRecCNData";

export default function PostedInvoices(onOpenReceipt) {
  const [patTotal, setPatTotal] = useState(0);
  const [medTotal, setMedTotal] = useState(0);
  const [patReceiptTotal, setPatReceiptTotal] = useState(0);
  const [medReceiptTotal, setMedReceiptTotal] = useState(0);
  const [patCreditTotal, setPatCreditTotal] = useState(0);
  const [medCreditTotal, setMedCreditTotal] = useState(0);
  const [patRefundTotal, setPatRefundTotal] = useState(0);
  const [medRefundTotal, setMedRefundTotal] = useState(0);
  const [invoice, setInvoice] = useState({});
  const [account, setAccount] = useState({});
  const [patient, setPatients] = useState({});
  const [practice, setPractice] = useState({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [practitioner, setPractitioner] = useState({});
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const navigate = useNavigate();
  const [loadingClaim, setLoadingClaim] = useState(false);
  const [userId, setUserId] = useState(null);
  const [claimId, setClaimId] = useState(null);
  const [isServiceUnavailable, setIsServiceUnavailable] = useState(false);
  const [message, setMessage] = useState("");
  const [statementBlob, setStatementBlob] = useState(null);
  const [showLineInfo, setShowLineInfo] = useState(false);
  const [selectedClaimLine, setSelectedClaimLine] = useState(null);
  const [banking, setBanking] = useState([]);
  const [manualSubmit, setManualSubmit] = useState(false);
  const [openReceipts, setOpenReceipts] = useState(true);
  const [openCase, setOpenCase] = useState(true);
  const [openClaim, setOpenClaim] = useState(true);
  const [claim, setClaim] = useState({
    status: "Pending Submission",
    transactionNumber: "",
    message: "",
    financials: {
      submittedAmount: "",
      approvedGross: "",
      approvedNett: "",
      discount: "",
      surcharges: "",
      copay: "",
      levy: "",
      overCharged: "",
      feeOverCharged: "",
      calculatedGross: "",
    },
    patientClaimFinancials: {
      claimFinancialDescription: "",
      claimFinancialAmount: "",
    },
    overAllWarnings: {
      code: "",
      description: "",
    },
    overAllRejections: {
      code: "",
      description: "",
    },
    overAllFundWarnings: {
      code: "",
      description: "",
    },
    overAllFundRejections: {
      code: "",
      description: "",
    },
    hnetNumber: "",
    mediKreditMsg: "",
    lineResults: [
      {
        lineNumber: "",
        status: "",
        warnings: "",
        rejections: "",
        tariffCode: "",
        grossAmount: "",
      },
    ],
    accountId: "",
    invoiceId: "",
    practiceId: "",
    patientId: "",
  });

  const { id } = useParams(); // Get the invoiceId from the URL parameters

  useEffect(() => {
    const getInvoiceData = async () => {
      try {
        const token = sessionStorage.getItem("user");
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken._id);
        const response = await getInvoiceById(id); // Replace with your API endpoint
        setInvoice(response);
        const accountData = await getAccountById(response.accountId); // Fetch account data using the accountId from the invoice
        setAccount(accountData);
        const patientData = await getPatientById(response.patient.patientId); // Fetch patient data using the patientId from the invoice
        setPatients(patientData);
        const practiceData = await getPracticeById(
          response.practice.practiceNumber
        ); // Fetch practice data using the practiceId from the invoice
        setPractice(practiceData);
        const practitionerData = await getUserById(response.practitioner.id); // Fetch practitioner data using the practitionerId from the invoice
        setPractitioner(practitionerData);
        const claimData = await getClaimByInvoiceId(id); // Fetch claim data using the invoiceId
        if (claimData) {
          setClaim(claimData);
          setClaimId(claimData._id);
        } else {
          console.log("No claim data found for this invoice.");
        }
      } catch (error) {
        console.error("Error fetching invoices:", error);
      }
    };
    getInvoiceData();
  }, [id]);

  useEffect(() => {
    if (claim) {
      setClaimId(claim?._id);
    }
  }, [claim]);

  async function handlePrint() {
    if (invoice) {
      // Create base invoice data object
      const invoiceData = {
        ...invoice,
        account,
        practice,
        practitioner,
      };

      try {
        // Get raw banking data
        const rawBankingData = await getBankingDetailsByPracticeNumbers([
          invoiceData.practice.practiceNumber,
        ]);

        // Ensure banking data exists before attempting to decrypt
        if (rawBankingData && rawBankingData.length > 0) {
          // Decrypt banking data
          const decryptedBankingData = await Promise.all(
            rawBankingData.map(async (entry) => {
              const decryptedAccountNumber = entry.bank?.accountNumber
                ? await decryptField(entry.bank.accountNumber)
                : "";
              const decryptedAccountName = entry.bank?.accountName
                ? await decryptField(entry.bank.accountName)
                : "";

              return {
                ...entry,
                bank: {
                  ...entry.bank,
                  accountNumber: decryptedAccountNumber,
                  accountName: decryptedAccountName,
                },
              };
            })
          );

          // Set banking data in state for component use
          setBanking(decryptedBankingData);

          // Important: Directly assign the decrypted banking data to the invoiceData
          // before exporting to PDF
          invoiceData.banking = decryptedBankingData;
        } else {
          console.warn(
            "No banking details found for practice number:",
            invoiceData.practice.practiceNumber
          );
          invoiceData.banking = [];
        }

        // Export the PDF with the complete data including decrypted banking details
        await ExportPDFInvoice(invoiceData);
        console.log("exportInvoiceData", invoiceData);
      } catch (error) {
        console.error("Error in handlePrint:", error);
        // Still try to export the PDF even if banking details failed
        await ExportPDFInvoice(invoiceData);
      }
    } else {
      console.warn("No invoice data available for printing");
    }
  }

  async function prepareEmailInvoice() {
    try {
      if (invoice) {
        const invoiceData = {
          ...invoice,
          account,
          practice,
          practitioner,
        };
        const rawBankingData = await getBankingDetailsByPracticeNumbers([
          invoiceData.practice.practiceNumber,
        ]);

        // Ensure banking data exists before attempting to decrypt
        if (rawBankingData && rawBankingData.length > 0) {
          // Decrypt banking data
          const decryptedBankingData = await Promise.all(
            rawBankingData.map(async (entry) => {
              const decryptedAccountNumber = entry.bank?.accountNumber
                ? await decryptField(entry.bank.accountNumber)
                : "";
              const decryptedAccountName = entry.bank?.accountName
                ? await decryptField(entry.bank.accountName)
                : "";

              return {
                ...entry,
                bank: {
                  ...entry.bank,
                  accountNumber: decryptedAccountNumber,
                  accountName: decryptedAccountName,
                },
              };
            })
          );

          // Set banking data in state for component use
          setBanking(decryptedBankingData);

          // Important: Directly assign the decrypted banking data to the invoiceData
          // before exporting to PDF
          invoiceData.banking = decryptedBankingData;
        } else {
          console.warn(
            "No banking details found for practice number:",
            invoiceData.practice.practiceNumber
          );
          invoiceData.banking = [];
        }

        // Call ExportPDFInvoice with asBlob: true directly
        const blob = await ExportPDFInvoice(invoiceData, { asBlob: true });

        // Only continue if we got a blob back
        if (blob) {
          setStatementBlob(blob);
          setShowEmailModal(true);
        }
      }
    } catch (error) {
      console.error("❌ Failed to generate invoice PDF:", error);
    }
  }

  const handleSubmitClaim = async () => {
    setLoadingClaim(true);
    setIsServiceUnavailable(false);
    console.log("SubmitClaim data:", {
      invoice,
      account,
      practice,
      selectedPatient: patient,
    });

    try {
      const response = await SubmitClaim({
        invoice,
        account,
        practice,
        selectedPatient: patient,
        claimtxn: "",
      });
      console.log("SubmitClaim data:", {
        invoice,
        account,
        practice,
        selectedPatient: patient,
      });
      const lineStatuses = (response.lineResults || []).map((l) =>
        (l?.status || "").toUpperCase()
      );

      let computedStatus;
      if (lineStatuses.length > 0) {
        if (lineStatuses.every((s) => s === "R")) {
          computedStatus = "Switch Rejected";
        } else if (lineStatuses.some((s) => s === "R" || s === "W")) {
          const mediKreditStatus = (
            response.mediKreditStatus || ""
          ).toLowerCase();
          if (
            mediKreditStatus === "p" &&
            !response.overAllRejections &&
            !response.overAllFundRejections
          ) {
            computedStatus = "Processed - Item Level Warnings Or Rejections";
          } else {
            computedStatus = "Rejected";
          }
          if (mediKreditStatus === "p") {
            computedStatus = "Processed";
          }
          setMessage("Item Level Warnings Or Rejections");
        } else {
          computedStatus = "Processed";
        }
      } else {
        // fallback if no lineResults
        const code = (response.mediKreditStatus || "").toLowerCase();
        computedStatus =
          code === "p"
            ? "Processed"
            : code === "r"
            ? "Switch Rejected"
            : code === "w"
            ? "Item Level Warnings Or Rejections"
            : "Pending";
      }

      const updatedClaim = {
        ...response,
        status: computedStatus,
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        createdBy: userId,
        createdDate: new Date().toISOString(),
      };
      setClaim(updatedClaim);
      const claimCreated = await createClaim(updatedClaim);

      const newInvoice = {
        ...invoice,
        status: updatedClaim?.status.includes("Processed")
          ? "Posted - Claim Processed"
          : "Posted - Claim in Progress",
      };
      setInvoice(newInvoice);
      await updateInvoice(newInvoice?._id, newInvoice);
      setClaimId(claimCreated.claimId);
    } catch (err) {
      console.error("🚨 Error in claim submission:", err);
      setIsServiceUnavailable(true);
    } finally {
      setLoadingClaim(false);
    }
  };

  const handleReceipt = () => {
    setShowReceiptModal(true);
  };

  const handleUpdateClaim = () => {
    navigate(`/invoices/${invoice?._id}/${account?._id}`, {
    // state: { initialInvoiceApp: invoice }, // ✅ pass full invoice
          
    });
  };

  async function handleUpdateInvoice() {
    navigate(`/invoices/${invoice?._id}/${account?._id}`, {
    });
  };

  async function handleResubmitClaim() {
    setLoadingClaim(true);
    setIsServiceUnavailable(false);
    setClaimId(claim?._id);
    try {
      const response = await SubmitClaim({
        invoice,
        account,
        practice,
        selectedPatient: patient,
        claimtxn: claim.transactionNumber,
      });

      const lineStatuses = (response.lineResults || []).map((l) =>
        (l.status || "").toUpperCase()
      );

      let computedStatus;
      if (lineStatuses.length > 0) {
        if (lineStatuses.every((s) => s === "R")) {
          computedStatus = "Switch Rejected";
        } else if (lineStatuses.some((s) => s === "R" || s === "W")) {
          const mkStatus = (response.mediKreditStatus || "").toLowerCase();
          computedStatus =
            mkStatus === "p"
              ? "Processed - Item Level Warnings Or Rejections"
              : "Processed";
          setMessage("Item Level Warnings Or Rejections");
        } else {
          computedStatus = "Processed";
        }
      } else {
        const code = (response.mediKreditStatus || "").toLowerCase();
        computedStatus =
          code === "p"
            ? "Processed"
            : code === "r"
            ? "Switch Rejected"
            : code === "w"
            ? "Item Level Warnings Or Rejections"
            : "Pending";
      }

      const updatedClaim = {
        ...response,
        _id: claimId,
        status: computedStatus,
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        action: "Resubmitted",
        userId: userId,
      };

      // Save claim
      setClaim(updatedClaim);
      await updateClaim(updatedClaim?._id, updatedClaim);

      const updatedInvoice = {
        ...invoice,
        status: updatedClaim?.status.includes("Processed")
          ? "Posted - Claim Processed"
          : "Posted - Claim in Progress",
      };

      setInvoice(updatedInvoice);
      await updateInvoice(updatedInvoice?._id, updatedInvoice);
    } catch (err) {
      console.error("🚨 Error resubmitting claim:", err);
      setIsServiceUnavailable(true);
    } finally {
      setLoadingClaim(false);
    }
  }

  async function handleReverseClaim() {
    setLoadingClaim(true);
    try {
      const reverseClaim = await ReverseClaim({
        account,
        invoice,
        practice: practice,
        selectedPatient: patient,
        claimtxn: claim?.transactionNumber,
      });

      // Determine status based on MK status code
      const mediKreditStatus = (status) => {
        switch (status?.toLowerCase()) {
          case "p":
            return "Processed - Claim Reversed";
          case "r":
            return "Reversal Switch Rejected";
          case "w":
            return "Processed - Item Level Warnings Or Rejections";
          case "a":
            return "Approved - Claim Reversed";
          default:
            return "Pending";
        }
      };

      // Update local claim object
      const updatedClaimData = {
        ...reverseClaim,
        _id: claimId,
        lineResults: [],
        status: mediKreditStatus(reverseClaim?.mediKreditStatus),
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        action: "Reversed",
        userId: userId,
      };
      setClaim(updatedClaimData);

      // Update local invoice object
      const updatedInvoiceData = {
        ...invoice,
        status: "Claim In Progress",
      };

      setInvoice(updatedInvoiceData);

      // Persist both to DB
      await updateInvoice(invoice?._id, updatedInvoiceData);
      const updatedClaim = await updateClaim(claimId, updatedClaimData);

      setClaimId(updatedClaim.claimId);
    } catch (err) {
      console.error("🚨 Error in claim update:", err);
      setIsServiceUnavailable(true);
    } finally {
      setLoadingClaim(false);
    }
  }

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "draft":
        return "warning";
      case "pending":
        return "secondary";
      case "update posted":
        return "danger";
      case "posted":
        return "success";
      case "claim - processed":
        return "success";
      case "posted - claim in progress":
        return "secondary";
      case "paid":
        return "primary";
      case "claim in progress":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "dark";
    }
  };

  const getClaimStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "pending submission":
        return "dark";
      case "Switch Rejected":
        return "danger";
      case "processed":
        return "success";
      case "claim reversed":
        return "secondary";
      case "claim updated":
        return "primary";
      case "item level rejections or warnings":
        return "warning";
      case "reversal processed":
        return "success";
      case "processed by the medical aid":
        return "success";
      case "rejected by the medical aid":
        return "danger";
      case "reversal rejected":
        return "danger";
      default:
        return "dark";
    }
  };

  const getStatusText = (code) => {
    switch (code) {
      case "R":
        return "Rejected";
      case "P":
        return "Processed";
      case "W":
        return "Processed with Warnings";
      case "A":
        return "Approved";
      default:
        return code || "-";
    }
  };

  const getStatusVariantLines = (code) => {
    switch (code) {
      case "R":
        return "danger";
      case "P":
        return "primary";
      case "W":
        return "secondary";
      case "A":
        return "success";
      default:
        return "muted";
    }
  };

  async function handleManualClaim() {
    setLoadingClaim(true);
    setIsServiceUnavailable(false);
    try {
      const updatedClaim = {
        status: "Manual Submission",
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        createdBy: userId,
        createdDate: new Date().toISOString(),
      };
      setClaim(updatedClaim);
      const claimCreated = await createClaim(updatedClaim);

      const newInvoice = {
        ...invoice,
        status: "Posted - Manaul Submission",
      };
      setInvoice(newInvoice);
      await updateInvoice(invoice?._id, newInvoice);
      setClaimId(claimCreated.claimId);
      setManualSubmit(true);
    } catch (err) {
      console.error("🚨 Error in claim submission:", err);
      setIsServiceUnavailable(true);
    } finally {
      setLoadingClaim(false);
    }
  }

  async function handleManualResubmit() {
    setLoadingClaim(true);
    setIsServiceUnavailable(false);
    setClaimId(claim?._id);
    try {
      const updatedClaim = {
        _id: claimId,
        status: "Manual Resubmission",
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        action: "Resubmitted",
        userId: userId,
      };
      setClaim(updatedClaim);
      await updateClaim(updatedClaim?._id, updatedClaim);

      const updatedInvoice = {
        ...invoice,
        status: updatedClaim?.status.includes("Posted")
          ? "Posted - Manual Submission"
          : "Posted - Manual Submission",
      };
      setManualSubmit(true);
      setInvoice(updatedInvoice);
      await updateInvoice(updatedInvoice?._id, updatedInvoice);
    } catch (err) {
      console.error("🚨 Error resubmitting claim:", err);
    } finally {
      setLoadingClaim(false);
    }
  }

  const STATUS_BUTTONS = {
    "Pending Submission": [
      { label: "Update Claim", onClick: handleUpdateClaim, icon: "fa-pen" },
      {
        label: "E-Submission",
        onClick: handleSubmitClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Manual Submission",
        onClick: handleManualClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    Processed: [
      {
        label: "Reverse Claim",
        onClick: handleReverseClaim,
        icon: "fa-rotate-left",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Processed - Not Paid": [
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Processed - Item Level Warnings Or Rejections": [
      {
        label: "Reverse Claim",
        onClick: handleReverseClaim,
        icon: "fa-rotate-left",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Switch Rejected": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Manual Submission": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Manual Resubmission": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Approved - Claim Reversed": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Processed - Claim Reversed": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Claim Updated": [
      {
        label: "E-Resubmission",
        onClick: handleResubmitClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Manual Resubmission",
        onClick: handleManualResubmit,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Rejected - Medical Aid": [
      {
        label: "Reverse Claim",
        onClick: handleReverseClaim,
        icon: "fa-rotate-left",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Resubmit Claim",
        onClick: handleResubmitClaim,
        icon: "fa-file-invoice-dollar",
      },
    ],
    "Processed - Medical Aid": [
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Reversal Switch Rejected": [
      {
        label: "Reverse Claim",
        onClick: handleReverseClaim,
        icon: "fa-rotate-left",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    Paid: [{}],
  };

  const buttons = STATUS_BUTTONS[claim?.status] || [];

  const buttonRows = [];
  for (let i = 0; i < buttons.length; i += 2) {
    buttonRows.push([buttons[i], buttons[i + 1]]);
  }

  // Place these outside the return/render block
  async function calculateAndSaveLineAmounts(invoice, claim) {
    // Guard
    if (!invoice || !Array.isArray(invoice.lines)) {
      return { patientTotal: 0, medAidTotal: 0 };
    }

    const round2 = (n) => Number((parseFloat(n ?? 0) || 0).toFixed(2));
    const cleanNumber = (v) =>
      round2(
        String(v ?? 0)
          .toString()
          .replace(/[, ]+/g, "")
      );

    // Step 1: update each line with patientAmount / medAidAmount
    const hasScheme = Boolean(invoice?.medicalAid?.name);

    invoice.lines = invoice.lines.map((line) => {
      const full = cleanNumber(line.rate);
      const claimLine = claim?.lineResults?.find(
        (c) => String(c.lineNumber) === String(line.lineNumber)
      );

      let medAidAmt = 0;
      let patientAmt = 0;

      if (!hasScheme || line.privatePayment) {
        // No scheme on account, or explicitly private → patient pays full
        patientAmt = full;
      } else if (!claimLine) {
        // Scheme present but no claim feedback yet
        // If there is a billable code (tariff or nappi), allocate to scheme
        const hasBillableCode = Boolean(line.tariffCode || line.nappiCode);
        medAidAmt = hasBillableCode ? full : 0;
        patientAmt = hasBillableCode ? 0 : full;
      } else {
        // We have claim feedback: split by approved nett
        const approved = cleanNumber(claimLine.approvedNettAmount);
        // Clamp to avoid weird negatives/over-allocations
        medAidAmt = Math.max(0, Math.min(approved, full));
        patientAmt = round2(full - medAidAmt);
      }

      return {
        ...line,
        patientAmount: round2(patientAmt),
        medAidAmount: round2(medAidAmt),
      };
    });

    // Step 2: totals
    const totals = invoice.lines.reduce(
      (acc, line) => {
        acc.patientTotal = round2(acc.patientTotal + (line.patientAmount || 0));
        acc.medAidTotal = round2(acc.medAidTotal + (line.medAidAmount || 0));
        return acc;
      },
      { patientTotal: 0, medAidTotal: 0 }
    );

    // Step 3: receipts / credit-notes / refunds per bucket
    const sumPaymentsFor = (bucket) =>
      (invoice.receipts || []).reduce((sum, r) => {
        const src = String(r.paymentSource || "")
          .toLowerCase()
          .replace(/\s+/g, ""); // "medical aid" → "medicalaid"
        const match =
          bucket === "patient" ? src === "patient" : src === "medicalaid";
        if (!match) return sum;

        const receiptAmt = cleanNumber(r.receiptAmount);
        const creditAmt = cleanNumber(r.creditNoteAmount);
        const refundAmt = cleanNumber(r.refundAmount);

        // receipts + credits reduce outstanding; refunds increase it
        return round2(sum + receiptAmt + creditAmt - refundAmt);
      }, 0);

    const patientReceipts = sumPaymentsFor("patient");
    const medAidReceipts = sumPaymentsFor("medicalAid");

    // Step 4: outstanding & paid
    const patientOutstanding = round2(totals.patientTotal - patientReceipts);
    const medAidOutstanding = round2(totals.medAidTotal - medAidReceipts);
    const totalOutstanding = round2(patientOutstanding + medAidOutstanding);
    const paidAmount = round2(patientReceipts + medAidReceipts);

    // Step 5: write back to invoice
    invoice.patientTotal = totals.patientTotal;
    invoice.medAidTotal = totals.medAidTotal;
    invoice.outstandingBalance = {
      patientOutstanding,
      medAidOutstanding,
      totalOutstanding,
      paidAmount,
    };

    await updateInvoice(invoice?._id, invoice);
    return {
      patientTotal: totals.patientTotal,
      medAidTotal: totals.medAidTotal,
    };
  }

  useEffect(() => {
    if (invoice?._id && claim?.lineResults?.length > 0) {
      calculateAndSaveLineAmounts(invoice, claim)
        .then(({ patientTotal, medAidTotal }) => {
          setPatTotal(patientTotal);
          setMedTotal(medAidTotal);
        })
        .catch((err) => {
          console.error("🚨 Failed to update invoice:", err);
        });
    }
  }, [invoice, claim]);

  useEffect(() => {
    if (invoice?._id && Array.isArray(invoice.receipts)) {
      // initialize all six totals
      let totalPatientReceipts = 0;
      let totalMedicalAidReceipts = 0;
      let totalPatientCreditNotes = 0;
      let totalMedicalAidCreditNotes = 0;
      let totalPatientRefunds = 0;
      let totalMedicalAidRefunds = 0;

      invoice.receipts.forEach((r) => {
        // normalize source key to lower-case for consistency
        const src = (r.paymentSource || "").toString().toLowerCase();

        // 1) Receipts
        if (r.receiptAmount != null) {
          const amt = parseFloat(r.receiptAmount) || 0;
          if (src === "patient") totalPatientReceipts += amt;
          else if (src === "medicalaid" || src === "medical aid")
            totalMedicalAidReceipts += amt;
        }

        // 2) Credit-notes
        if (r.creditNoteAmount != null) {
          const amt = parseFloat(r.creditNoteAmount) || 0;
          if (src === "patient") totalPatientCreditNotes += amt;
          else if (src === "medicalaid" || src === "medical aid")
            totalMedicalAidCreditNotes += amt;
        }

        // 3) Refunds
        if (r.refundAmount != null) {
          const amt = parseFloat(r.refundAmount) || 0;
          if (src === "patient") totalPatientRefunds += amt;
          else if (src === "medicalaid" || src === "medical aid")
            totalMedicalAidRefunds += amt;
        }
      });

      // set them all into state
      setPatReceiptTotal(totalPatientReceipts);
      setMedReceiptTotal(totalMedicalAidReceipts);
      setPatCreditTotal(totalPatientCreditNotes);
      setMedCreditTotal(totalMedicalAidCreditNotes);
      setPatRefundTotal(totalPatientRefunds);
      setMedRefundTotal(totalMedicalAidRefunds);
    }
  }, [invoice]);

  const F = (x) =>
    x > 0
      ? new Intl.NumberFormat("en-ZA", {
          style: "currency",
          currency: "ZAR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(x)
      : "-";

  const ICD10Display = ({ oti, primaryCode, secondaryCodes = [] }) => {
    // Function to chunk array into groups of 2
   const chunkArray = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const secondaryChunks = chunkArray(secondaryCodes, 2);

  return (
    <div className="text-center">
      {/* Primary ICD10 Code */}
      <div className="fw-semibold text-center mb-1 d-flex justify-content-center align-items-center gap-2">
        {oti === "PB" && (
          <span className="badge bg-success text-white small px-2 py-1">PMB</span>
        )}
        <span>{primaryCode}</span>
        
      </div>

      {/* Secondary ICD10 Codes */}
      {secondaryChunks.map((chunk, index) => (
        <div key={index} className="text-center mb-1">
          {chunk.map((code, codeIndex) => (
            <span key={codeIndex} className="me-1">
              {code}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
};

  const prettyDT = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return String(iso);

    const pad = (n) => String(n).padStart(2, "0");

    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1); // months are 0-based
    const year = String(d.getFullYear()).slice(-2); // last 2 digits
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());

    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  return (
    <div>
      {manualSubmit && (
        <Modal show={manualSubmit} onHide={() => setManualSubmit(false)}>
          <Modal.Header>
            <Modal.Title className="text-primary">
              Manual Submission
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              You have marked this claim as manually submitted. There will be no
              action from PractoSync, and you will need to follow up with the
              medical aid directly.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setManualSubmit(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      <Container
        fluid
        className="bg-white shadow-sm pt-1 pb-1"
        style={{ marginTop: "75px" }}
      >
        <Row className="align-items-center justify-content-end mb-1 gx-0">
          <Col md={6} className="ps-0 pe-0">
            <h5 className="text-primary text-start mb-1 fw-normal">
              INVOICE &amp; CLAIM
            </h5>
          </Col>

          <Col
            md={5}
            className="d-flex align-items-center justify-content-end gap-2 me-1"
          >
            <h6
              className={`mb-0 fw-bold me-4 text-${getStatusVariant(
                invoice?.status
              )}`}
            >
              {invoice?.status}
            </h6>

            <Button
              variant="secondary"
              size="lg"
              className="rounded-0 w-25"
              onClick={prepareEmailInvoice}
            >
              <i className="fa-regular fa-envelope me-1" />
              Email
            </Button>

            <Button
              variant="secondary"
              size="lg"
              className="rounded-0 w-25 me-1"
              onClick={handlePrint}
            >
              <i className="fa fa-download me-1" />
              Download
            </Button>
          </Col>
        </Row>
      </Container>

      <Row className="ms-2 me-2 gx-2 mt-1">
        {/* Left: Invoice header + lines */}
        <Col md={9}>
          <Container className="border shadow-sm p-3">
            {/* --- Invoice Header --- */}
            <Row>
              <Col md={3}>
                <h6 className="mb-2 mt-1 text-primary text-start border-bottom border-primary">
                  Account
                </h6>
                <p className="ms-1 mb-1 fw-normal">
                  {account?.accountTitle} {account?.accountFullNames}{" "}
                  {account?.accountLastName}
                </p>
                <p className="ms-1 mb-0">{account?.accountHomeAddress1}</p>
                <p className="ms-1 mb-1">
                  {account?.accountHomeAddress2}{" "}
                  {account?.accountHomeAddressCity}{" "}
                  {account?.accountHomeAddressPostalCode}
                </p>
                <p className="ms-1 mb-0">{account?.accountTelNumberCell}</p>
                <p className="ms-1 mb-0">{account?.accountEmail}</p>
              </Col>

              <Col md={3}>
                <h6 className="mb-2 mt-1 text-primary text-start border-bottom border-primary">
                  Patient
                </h6>
                <p className="ms-1 mb-1 fw-normal">
                  {patient?.patientTitle} {patient?.patientFullNames} (
                  {patient?.patientInitials}) {patient?.patientLastName}
                </p>
                {account.accountMedicalAid && (
                  <>
                    <p className="ms-1 mb-0">{account?.accountMedicalAid}</p>
                    <p className="ms-1 mb-0">
                      {account?.accountMedicalAidPlan} |{" "}
                      {account?.accountMedicalAidOption}
                    </p>
                    <p className="ms-1 mb-0">
                      {account?.accountMedicalAidNumber} (
                      {patient?.patientMedicalAidDependantCode})
                    </p>
                    <p
                      className={`ms-1 mb-0 fw-bold ${
                        ["Suspended", "Resigned"].includes(
                          patient?.patientMedicalAidStatus
                        )
                          ? "text-warning"
                          : "text-success"
                      }`}
                    >
                      {patient?.patientMedicalAidStatus}
                    </p>
                  </>
                )}
              </Col>

              <Col md={3}>
                <h6 className="mb-2 mt-1 text-primary text-start border-bottom border-primary">
                  Practice &amp; Practitioner
                </h6>
                <p className="ms-1 text-uppercase mb-1 fw-normal">
                  {invoice?.practice?.practiceName}
                </p>
                <p className="ms-1 mb-0">
                  BHF: {invoice?.practice?.practiceNumber}
                </p>
                <p className="ms-1 mb-0">
                  VAT: {invoice?.practice?.vatNumber || "-"}
                </p>
                <p className="ms-1 mb-0 text-uppercase">
                  {invoice?.practitioner?.firstName}{" "}
                  {invoice?.practitioner?.lastName}
                </p>
                <p className="ms-1 mb-0">
                  HPCSA: {invoice?.practitioner?.hpcsa}
                </p>
              </Col>

              <Col md={3}>
                <h6 className="mb-2 mt-1 text-primary text-start border-bottom border-primary">
                  Invoice Info
                </h6>
                <Row className="mb-1">
                  <Col xs={4}>Invoice:</Col>
                  <Col xs={8}>{invoice?.invoiceNumber}</Col>
                </Row>
                <Row className="mb-1">
                  <Col xs={4}>Date:</Col>
                  <Col xs={8}>{invoice?.invoiceDate}</Col>
                </Row>
              </Col>
            </Row>
            <Row
              className="fw-normal text-primary mb-1 mt-4 ms-1 border-bottom border-top border-primary me-2"
              style={{ backgroundColor: "rgb(206.6, 226, 254.6)" }}
            >
              <Col md={2} className="text-center fw-bold">
                Date
              </Col>
              <Col md={1} className="text-center fw-bold">
                ICD10
              </Col>
              <Col md={1} className="text-center fw-bold">
                Code | (Modifier)
              </Col>
              <Col md={4} className="text-center fw-bold">
                Description
              </Col>
              <Col md={1} className="text-center fw-bold">
                Unit(s)
              </Col>
              <Col md={1} className="text-center fw-bold">
                Patient
              </Col>
              <Col md={1} className="text-center fw-bold">
                Med Aid
              </Col>
              <Col md={1} className="text-center fw-bold">
                Total
              </Col>
            </Row>

            {/* --- Line Items --- */}

            <>
              {invoice?.lines?.map((line, i) => {
                const claimLine = claim?.lineResults?.find(
                  (c) => String(c.lineNumber) === String(line.lineNumber)
                );

                const full = parseFloat(line.rate) || 0;
                const hasScheme = !!invoice.medicalAid?.name;

                let medAidAmt = 0;
                let patientAmt = 0;
                const totalAmt = full;

                if (!hasScheme || line.privatePayment) {
                  patientAmt = full;
                } else if (hasScheme && !claimLine) {
                  if (line.tariffCode || line.nappiCode) {
                    medAidAmt = full;
                  } else {
                    patientAmt = full;
                  }
                } else {
                  const approved = parseFloat(
                    claimLine?.approvedNettAmount ?? 0
                  );
                  medAidAmt = approved;
                  if (approved !== full) {
                    patientAmt = full - approved;
                  }
                }

                return (
                  <React.Fragment key={i}>
                    {claimLine && (
                      <>
                        <Row className="mb-1 ms-1 me-2 align-items-center">
                          <Col
                            md={3}
                            className={`fw-bold text-start text-${getStatusVariantLines(
                              claimLine?.status
                            )}`}
                          >
                            {getStatusText(claimLine.status)}
                          </Col>

                          <Col
                            md={4}
                            className={`fw-bold text-end text-${getStatusVariantLines(
                              claimLine?.status
                            )}`}
                          ></Col>

                          <Col
                            md={4}
                            className={`fw-bold text-end text-${getStatusVariantLines(
                              claimLine?.status
                            )}`}
                          >
                            <div className="d-flex align-items-center justify-content-end">
                              <span className="me-2">
                                Approved Nett Amount:
                              </span>
                              <span>
                                {claimLine?.approvedNettAmount != null
                                  ? F(Number(claimLine?.approvedNettAmount))
                                  : "R 0.00"}
                              </span>
                            </div>
                          </Col>
                          <Col md={1} className="fw-bold text-end">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 ms-2"
                              onClick={() => {
                                setSelectedClaimLine(claimLine);
                                setShowLineInfo(true);
                              }}
                            >
                              <i
                                className={`fa fa-info-circle text-${getStatusVariantLines(
                                  claimLine?.status
                                )}`}
                              ></i>
                            </Button>
                          </Col>
                        </Row>
                      </>
                    )}

                    <Row className="mb-1 ms-1 border-bottom border-gray me-2">
                      <Col md={2} className="text-center">
                        {line.appointmentCustomStart ? (
                          <>
                            <div>{prettyDT(line.appointmentCustomStart)}</div>
                            <div>to</div>
                            <div>{prettyDT(line.appointmentCustomEnd)}</div>
                          </>
                        ) : line.appointmentStartSet ? (
                          <>
                            <div>{prettyDT(line.appointmentStartSet)}</div>
                            <div>to</div>
                            <div>{prettyDT(line.appointmentEndSet)}</div>
                          </>
                        ) : (
                          <div>
                            {line.appointmentDateCustom ||
                              line.appointmentDateSet ||
                              "-"}
                          </div>
                        )}
                      </Col>

                      <Col md={1} className="text-center">
                        <ICD10Display
                          oti={line.oti}
                          primaryCode={line.icd101}
                          secondaryCodes={
                            line.icd102
                              ? line.icd102
                                  .split(";")
                                  .map((code) => code.trim())
                                  .filter(Boolean)
                              : []
                          }
                        />
                      </Col>
                      <Col md={1} className="text-center">
                        {(() => {
                          // If it's a NAPPI line (either explicitly typed or has a code), show nappiCode.
                          const isNappi =
                            line.type === "nappi" || !!line.nappiCode;
                          if (isNappi) return line.nappiCode || "-";

                          // Otherwise show tariff with modifiers inline.
                          const mods = Array.isArray(line.modifier)
                            ? line.modifier
                                .map((m) =>
                                  typeof m === "string" ? m : m?.code
                                )
                                .filter(Boolean)
                            : [];

                          if (!line.tariffCode) return "-";
                          return mods.length
                            ? `${line.tariffCode} (${mods.join(", ")})`
                            : line.tariffCode;
                        })()}
                      </Col>

                      <Col md={4} className="text-start">
                        {line.description || "-"}
                      </Col>
                      <Col md={1} className="text-center">
                        {line.quantity || "-"}
                      </Col>
                      <Col md={1} className="text-end">
                        {F(patientAmt)}
                      </Col>
                      <Col md={1} className="text-end">
                        {F(medAidAmt)}
                      </Col>
                      <Col md={1} className="text-end fw-bold">
                        R{(totalAmt ?? 0).toFixed(2)}
                      </Col>
                    </Row>
                  </React.Fragment>
                );
              })}
              <Modal
                show={showLineInfo}
                onHide={() => setShowLineInfo(false)}
                size="lg"
                centered
              >
                <Modal.Header closeButton>
                  <Modal.Title>Line details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Row className="mb-1 me-2">
                    <p className="mb-0 fw-bold text-primary">
                      Line Number: {selectedClaimLine?.lineNumber}
                    </p>
                  </Row>

                  <p className="mb-0 text-primary border-bottom border-primary pb-2">
                    {selectedClaimLine?.tariffCode} -{" "}
                    {selectedClaimLine?.description}
                  </p>

                  <Row className="mb-1 me-2 mt-2 align-items-center">
                    <Col
                      md={4}
                      className={`fw-bold text-start text-${getStatusVariantLines(
                        selectedClaimLine?.status
                      )}`}
                    >
                      <h6>{getStatusText(selectedClaimLine?.status)}</h6>
                    </Col>
                  </Row>

                  <Col
                    md={5}
                    className={`text-start text-${getStatusVariantLines(
                      selectedClaimLine?.status
                    )}`}
                  >
                    {selectedClaimLine?.warnings?.map((warn, index) => (
                      <p className="mb-0" key={`warn-${index}`}>
                        {warn.code} - {warn.description}
                      </p>
                    ))}

                    {selectedClaimLine?.rejections?.map((rej, index) => (
                      <p className="mb-0" key={`rej-${index}`}>
                        {rej.code} - {rej.description}
                      </p>
                    ))}
                  </Col>

                  <Col md={3}></Col>

                  <hr />

                  <dl className="row">
                    <dt className="col-6">Submitted Amount</dt>
                    <dd className="col-6 text-end fw-bold">
                      {selectedClaimLine?.submittedGrossAmount
                        ? `R ${Number(
                            selectedClaimLine.submittedGrossAmount
                          ).toFixed(2)}`
                        : "R 0.00"}
                    </dd>
                    <dt className="col-6">Approved Gross</dt>
                    <dd className="col-6 text-end fw-bold">
                      {selectedClaimLine?.approvedGrossAmount
                        ? `R ${Number(
                            selectedClaimLine.approvedGrossAmount
                          ).toFixed(2)}`
                        : "R 0.00"}
                    </dd>
                    <dt className="col-6 fw-normal">Approved Discount</dt>
                    <dd className="col-6 text-end">
                      {selectedClaimLine?.approvedDiscount
                        ? `R ${Number(
                            selectedClaimLine.approvedDiscount
                          ).toFixed(2)}`
                        : "R 0.00"}
                    </dd>
                    <dt className="col-6 fw-normal">Approved Copay</dt>
                    <dd className="col-6 text-end">
                      {selectedClaimLine?.approvedCopay
                        ? `R ${Number(selectedClaimLine.approvedCopay).toFixed(
                            2
                          )}`
                        : "R 0.00"}
                    </dd>
                    <dt className="col-6 fw-normal">Approved Levy</dt>
                    <dd className="col-6 text-end">
                      {selectedClaimLine?.approvedLevy
                        ? `R ${Number(selectedClaimLine.approvedLevy).toFixed(
                            2
                          )}`
                        : "R 0.00"}
                    </dd>
                    <dt className="col-6">Approved Net</dt>
                    <dd className="col-6 text-end border-top fw-bold">
                      {selectedClaimLine?.approvedNettAmount
                        ? `R ${Number(
                            selectedClaimLine.approvedNettAmount
                          ).toFixed(2)}`
                        : "R 0.00"}
                    </dd>
                    <dt className="col-6 fw-bold">Over-charged</dt>
                    <dd className="col-6 text-end fw-bold">
                      {selectedClaimLine?.overCharged
                        ? `R ${Number(selectedClaimLine.overCharged).toFixed(
                            2
                          )}`
                        : "R 0.00"}
                    </dd>
                  </dl>

                  {/* ⬇️ Modifier Details */}
                  {Array.isArray(selectedClaimLine?.modifiers) &&
                    selectedClaimLine.modifiers.length > 0 && (
                      <>
                        <hr />
                        {selectedClaimLine.modifiers.map((mod, idx) => (
                          <div key={`mod-${idx}`} className="ms-3 mb-3">
                            <h6 className="text-primary">
                              Modifier:{" "}
                              {mod.modifierCode || mod.modifier || "Unknown"}
                            </h6>
                            <p
                              className={`ms-2 mb-1 fw-bold text-${getStatusVariantLines(
                                mod.status
                              )}`}
                            >
                              Status: {getStatusText(mod.status)}
                            </p>

                            {mod.warnings?.length > 0 && (
                              <div>
                                {mod.warnings.map((warn, wIdx) => (
                                  <p
                                    className="ms-2 mb-0 text-secondary"
                                    key={`mod-warn-${wIdx}`}
                                  >
                                    {warn.code} - {warn.description}
                                  </p>
                                ))}
                              </div>
                            )}

                            {mod.rejections?.length > 0 && (
                              <div>
                                {mod.rejections.map((rej, rIdx) => (
                                  <p
                                    className="ms-2 mb-0 text-danger"
                                    key={`mod-rej-${rIdx}`}
                                  >
                                    {rej.code} - {rej.description}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowLineInfo(false)}
                  >
                    Close
                  </Button>
                </Modal.Footer>
              </Modal>

              <Row className="text-end ms-4 mt-2 me-2">
                <Col md={8}></Col>
                <Col md={4}>
                  <Row className="align-items-end my-1 border-bottom border-1 border-primary">
                    <Col xs={9}>
                      <p className="mb-1 fw-bold text-primary">
                        Patient Total:
                      </p>
                    </Col>
                    <Col xs={3}>
                      <p className="mb-1 fw-bold text-primary text-end">
                        {F(patTotal)}
                      </p>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row className="text-end ms-4 mt-2 me-2">
                <Col md={8}></Col>
                <Col md={4}>
                  <Row className="align-items-end my-1 border-bottom border-1 border-primary">
                    <Col xs={9}>
                      <p className="mb-1 fw-bold text-primary">
                        Medical Aid Total:
                      </p>
                    </Col>
                    <Col xs={3}>
                      <p className="mb-1 fw-bold text-primary text-end">
                        {F(medTotal)}
                      </p>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </>

            {/* Invoice Total */}
            <Row className="text-end ms-4 mt-1 me-2">
              <Col md={8}></Col>
              <Col md={4}>
                <Row className="align-items-end my-1 border-bottom border-4 border-primary">
                  <Col xs={9}>
                    <p className="mb-1 fw-bold text-primary">Invoice Total:</p>
                  </Col>
                  <Col xs={3}>
                    <p className="mb-1 fw-bold text-primary text-end">
                      {invoice?.totalAmount
                        ? new Intl.NumberFormat("en-ZA", {
                            style: "currency",
                            currency: "ZAR",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(parseFloat(invoice?.totalAmount))
                        : "-"}
                    </p>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </Col>
        {invoice?.status !== "Posted" ? (
          <Col md={3}>
            {/* Receipts / Credit Notes / Refunds */}
            <Container className="border shadow-sm p-3 me-2">
              {/* Header */}
              <Row className="fw-bold text-primary mb-1 border-1 border-bottom border-primary">
                <Col className="d-flex align-items-center">
                  <h6 className="m-0 flex-grow-1 text-center">
                    Receipts / Credit Notes / Refunds
                  </h6>
                  <button
                    type="button"
                    className="btn btn-link p-0 ms-2 text-primary"
                    onClick={() => setOpenReceipts((v) => !v)}
                    aria-controls="receipts-body"
                    aria-expanded={openReceipts}
                    title={openReceipts ? "Collapse" : "Expand"}
                  >
                    <i
                      className={`fa-solid ${
                        openReceipts ? "fa-chevron-up" : "fa-chevron-down"
                      }`}
                    />
                  </button>
                </Col>
              </Row>

              <Collapse in={openReceipts}>
                <div id="receipts-body">
                  <Row className="gx-0 mb-2 pb-2"></Row>

                  {invoice?.receipts !== null ? (
                    invoice?.status === "Paid" ||
                    invoice?.status === "Partially Paid" ? (
                      <>
                        <Row className="gx-0 mb-1">
                          <Col xs={6} className="fw-normal text-primary">
                            Invoice Amount:
                          </Col>
                          <Col
                            xs={6}
                            className="text-primary text-end fw-normal"
                          >
                            {F(invoice?.totalAmount)}
                          </Col>
                        </Row>

                        {/* Receipts */}
                        {patReceiptTotal > 0 && (
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Paid by Patient:
                            </Col>
                            <Col
                              xs={6}
                              className="text-primary text-end fw-normal"
                            >
                              ({F(patReceiptTotal)})
                            </Col>
                          </Row>
                        )}
                        {medReceiptTotal > 0 && (
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Paid by Medical Aid:
                            </Col>
                            <Col
                              xs={6}
                              className="text-primary text-end fw-normal"
                            >
                              ({F(medReceiptTotal)})
                            </Col>
                          </Row>
                        )}

                        {/* Credit Notes */}
                        {patCreditTotal > 0 && (
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Credit Note to Patient:
                            </Col>
                            <Col xs={6} className="text-primary text-end">
                              ({F(patCreditTotal)})
                            </Col>
                          </Row>
                        )}
                        {medCreditTotal > 0 && (
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Credit Note to Medical Aid:
                            </Col>
                            <Col xs={6} className="text-primary text-end">
                              ({F(medCreditTotal)})
                            </Col>
                          </Row>
                        )}

                        {/* Refunds */}
                        {patRefundTotal > 0 && (
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Refund to Patient:
                            </Col>
                            <Col xs={6} className="text-primary text-end">
                              R {patRefundTotal.toFixed(2)}
                            </Col>
                          </Row>
                        )}
                        {medRefundTotal > 0 && (
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Refund to Medical Aid:
                            </Col>
                            <Col xs={6} className="text-primary text-end">
                              R {medRefundTotal.toFixed(2)}
                            </Col>
                          </Row>
                        )}

                        <Row className="gx-0 mb-1 mt-2 border-top border-2 border-primary mb-3">
                          <Col xs={6} className="fw-bold text-primary">
                            Total Outstanding:
                          </Col>
                          <Col xs={6} className="text-primary text-end fw-bold">
                            {new Intl.NumberFormat("en-ZA", {
                              style: "currency",
                              currency: "ZAR",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(
                              parseFloat(
                                invoice?.outstandingBalance.totalOutstanding
                              ).toFixed(2)
                            )}
                          </Col>
                        </Row>
                      </>
                    ) : (
                      <Row>
                        <Col className="text-muted small fst-italic text-center">
                          No paid / partial payments.
                        </Col>
                      </Row>
                    )
                  ) : (
                    <Row>
                      <Col className="text-muted small fst-italic text-center">
                        No receipts / credits / refunds.
                      </Col>
                    </Row>
                  )}
                </div>
              </Collapse>
            </Container>

            {/* Case */}
            {(invoice?.case || invoice?.inPatientCase) && (invoice?.medicalAid?.name) && (
              <Container className="border shadow-sm p-3 me-2 mt-3">
                {/* Header */}
                <Row className="fw-bold text-primary mb-1 border-1 border-bottom border-primary">
                  <Col className="d-flex align-items-center">
                    <h6 className="m-0 flex-grow-1 text-center">Case</h6>
                    <button
                      type="button"
                      className="btn btn-link p-0 ms-2 text-primary"
                      onClick={() => setOpenCase((v) => !v)}
                      aria-controls="case-body"
                      aria-expanded={openCase}
                      title={openCase ? "Collapse" : "Expand"}
                    >
                      <i
                        className={`fa-solid ${
                          openCase ? "fa-chevron-up" : "fa-chevron-down"
                        }`}
                      />
                    </button>
                  </Col>
                </Row>

                <Collapse in={openCase}>
                  <div id="case-body">
                    <Row className="gx-0 mb-1 pb-2"></Row>

                    {(() => {
                      const cRaw = invoice?.case ?? {};
                      const legacy = invoice?.inPatientCase ?? {};
                      const c = {
                        description:
                          cRaw.description ?? cRaw.caseDescription ?? "—",
                        referringDoctorName: cRaw.referringDoctorName ?? "—",
                        referringDoctorBHF:
                          cRaw.referringDoctorBHF ?? legacy.caseBHF ?? "—",
                        referringDoctorHPCSA:
                          cRaw.referringDoctorHPCSA ?? legacy.caseHPCSA ?? "—",
                        authorizedBy: cRaw.authorizedBy ?? "—",
                        authorizationNumber:
                          cRaw.authorizationNumber ??
                          legacy.caseAuthNumber ??
                          "—",
                        startDate: cRaw.startDate ?? cRaw.caseStartDate ?? "—",
                        endDate: cRaw.endDate ?? cRaw.caseEndDate ?? "—",
                        icd101: cRaw.icd101 ?? legacy.caseICD10 ?? "",
                        icd102: Array.isArray(cRaw.icd102) ? cRaw.icd102 : [],
                      };
                      const icdPrimary = c.icd101 || "—";
                      const icdSecondary = c.icd102.length
                        ? c.icd102.join(", ")
                        : "—";

                      return (
                        <>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Description:
                            </Col>
                            <Col xs={6}>{c.description}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Doctor:
                            </Col>
                            <Col xs={6}>{c.referringDoctorName}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Ref BHF:
                            </Col>
                            <Col xs={6}>{c.referringDoctorBHF}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Ref HPCSA:
                            </Col>
                            <Col xs={6}>{c.referringDoctorHPCSA}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Authorized By:
                            </Col>
                            <Col xs={6}>{c.authorizedBy}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Authorization #:
                            </Col>
                            <Col xs={6}>{c.authorizationNumber}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Start Date:
                            </Col>
                            <Col xs={6}>{c.startDate}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              End Date:
                            </Col>
                            <Col xs={6}>{c.endDate}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              ICD-10 (Primary):
                            </Col>
                            <Col xs={6}>{icdPrimary}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              ICD-10 (Secondary):
                            </Col>
                            <Col xs={6}>{icdSecondary}</Col>
                          </Row>
                        </>
                      );
                    })()}
                  </div>
                </Collapse>
              </Container>
            )}

            {/* Claim Status */}
            {invoice?.medicalAid?.name && ( 
            <Container className="border shadow-sm p-3 me-2 mt-3">
              {/* Header */}
              <Row className="fw-bold text-primary mb-1 border-1 border-bottom border-primary">
                <Col className="d-flex align-items-center">
                  <h6 className="m-0 flex-grow-1 text-center">Claim Status</h6>
                  <button
                    type="button"
                    className="btn btn-link p-0 ms-2 text-primary"
                    onClick={() => setOpenClaim((v) => !v)}
                    aria-controls="claim-body"
                    aria-expanded={openClaim}
                    title={openClaim ? "Collapse" : "Expand"}
                  >
                    <i
                      className={`fa-solid ${
                        openClaim ? "fa-chevron-up" : "fa-chevron-down"
                      }`}
                    />
                  </button>
                </Col>
              </Row>

              <Collapse in={openClaim}>
                <div id="claim-body">
                  {/* 1) Service unavailable? */}
                  {isServiceUnavailable ? (
                    <Row className="mb-3">
                      <Col>
                        <Alert variant="danger" className="text-center mb-0">
                          Service Unavailable
                        </Alert>
                      </Col>
                    </Row>
                  ) : loadingClaim ? (
                    <div className="d-flex justify-content-center py-3">
                      <Spinner
                        animation="border"
                        role="status"
                        variant="primary"
                      >
                        <span className="visually-hidden">Loading…</span>
                      </Spinner>
                    </div>
                  ) : (
                    <>
                      {/* Status */}
                      <Row
                        className={`text-${getClaimStatusVariant(
                          claim?.status
                        )} fw-bold text-center mb-1`}
                      >
                        <Col>
                          <h6>{claim?.status || "-"}</h6>
                        </Col>
                      </Row>

                      {/* Transaction & HNET */}
                      <Row className="mb-1">
                        <Col xs={6} className="text-primary">
                          Transaction #:
                        </Col>
                        <Col xs={6}>{claim?.transactionNumber || "-"}</Col>
                      </Row>
                      <Row className="mb-1">
                        <Col xs={6} className="text-primary">
                          HNET #:
                        </Col>
                        <Col xs={6}>{claim?.hnetNumber || "-"}</Col>
                      </Row>

                      {/* Messages */}
                      <Row className="text-primary mt-1 mb-1 ms-1">
                        {claim?.message}
                      </Row>

                      {/* Switch rejection */}
                      {claim?.overAllRejections?.description && (
                        <Row className="gx-0 mt-2 mb-1">
                          <Col xs={12} className="fw-bold text-danger">
                            Switch Rejection
                          </Col>
                          <Col xs={12} className="text-danger">
                            {claim.overAllRejections.description}
                          </Col>
                        </Row>
                      )}

                      {/* Medical Aid rejection */}
                      {claim?.overAllFundRejections?.description && (
                        <Row className="gx-0 mt-2 mb-1">
                          <Col xs={12} className="fw-bold text-danger">
                            Rejection
                          </Col>
                          <Col xs={12} className="text-danger">
                            {claim?.overAllFundRejections?.description}
                          </Col>
                        </Row>
                      )}

                      {/* Switch warning */}
                      {claim?.overAllWarnings?.description && (
                        <Row className="gx-0 mt-2 mb-1">
                          <Col xs={12} className="fw-bold text-secondary">
                            Messages / Warning
                          </Col>
                          <Col xs={12} className="text-secondary">
                            {claim.overAllWarnings.description}
                          </Col>
                        </Row>
                      )}

                      {/* Medical Aid warning */}
                      {claim?.overAllFundWarnings?.description && (
                        <Row className="gx-0 mt-2 mb-1">
                          <Col xs={12} className="fw-bold text-secondary">
                            Messages / Warning
                          </Col>
                          <Col xs={12} className="text-secondary">
                            {claim?.overAllFundWarnings?.description}
                          </Col>
                        </Row>
                      )}

                      {claim?.mediKreditMsg && (
                        <>
                          <Row className="text-secondary fw-bold mt-2 mb-1">
                            <Col>Message from the switch</Col>
                          </Row>
                          <Row className="text-secondary fw-bold">
                            {claim?.mediKreditMsg}
                          </Row>
                        </>
                      )}

                      {/* Financials */}
                      {claim?.financials?.approvedGross != null && (
                        <Row className="gx-0 mb-1">
                          <Col xs={6} className="fw-normal text-primary">
                            Approved Gross:
                          </Col>
                          <Col xs={6} className="text-primary text-end">
                            {new Intl.NumberFormat("en-ZA", {
                              style: "currency",
                              currency: "ZAR",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(claim.financials.approvedGross)}
                          </Col>
                        </Row>
                      )}
                      {claim?.financials?.copay != null && (
                        <Row className="gx-0 mb-1">
                          <Col xs={6} className="fw-normal text-primary">
                            Copay:
                          </Col>
                          <Col xs={6} className="text-danger text-end">
                            {new Intl.NumberFormat("en-ZA", {
                              style: "currency",
                              currency: "ZAR",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(claim.financials.copay)}
                          </Col>
                        </Row>
                      )}
                      {claim?.financials?.levy != null && (
                        <Row className="gx-0 mb-1">
                          <Col xs={6} className="fw-normal text-primary">
                            Levy:
                          </Col>
                          <Col xs={6} className="text-danger text-end">
                            {new Intl.NumberFormat("en-ZA", {
                              style: "currency",
                              currency: "ZAR",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(claim.financials.levy)}
                          </Col>
                        </Row>
                      )}
                      {claim?.financials?.discount != null && (
                        <Row className="gx-0 mb-1">
                          <Col xs={6} className="fw-normal text-primary">
                            Discount:
                          </Col>
                          <Col xs={6} className="text-danger text-end">
                            {new Intl.NumberFormat("en-ZA", {
                              style: "currency",
                              currency: "ZAR",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(claim?.financials.discount)}
                          </Col>
                        </Row>
                      )}
                      {claim?.financials?.approvedNett != null && (
                        <Row className="gx-0 mb-1 pt-1 border-top border-primary border-2">
                          <Col xs={6} className="fw-bold text-primary">
                            Approved Nett:
                          </Col>
                          <Col xs={6} className="text-primary fw-bold text-end">
                            {new Intl.NumberFormat("en-ZA", {
                              style: "currency",
                              currency: "ZAR",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(claim?.financials.approvedNett)}
                          </Col>
                        </Row>
                      )}
                      {claim?.financials?.submittedAmount != null && (
                        <Row className="gx-0 mb-1 pb-1 ">
                          <Col xs={6} className="fw-bold text-primary">
                            Submitted Amount:
                          </Col>
                          <Col xs={6} className="text-primary fw-bold text-end">
                            {new Intl.NumberFormat("en-ZA", {
                              style: "currency",
                              currency: "ZAR",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(claim?.financials.submittedAmount)}
                          </Col>
                        </Row>
                      )}
                      {claim?.financials?.surcharges?.schm != null && (
                        <Row className="gx-0 mb-1">
                          <Col xs={6} className="fw-normal text-primary">
                            Surcharged:
                          </Col>
                          <Col xs={6} className="text-danger text-end">
                            {new Intl.NumberFormat("en-ZA", {
                              style: "currency",
                              currency: "ZAR",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(claim?.financials.surcharges.schm)}
                          </Col>
                        </Row>
                      )}
                      {claim?.financials?.surcharges?.save != null && (
                        <Row className="gx-0 mb-1">
                          <Col xs={6} className="fw-normal text-primary">
                            Surcharge paid from savings:
                          </Col>
                          <Col xs={6} className="text-danger text-end">
                            {new Intl.NumberFormat("en-ZA", {
                              style: "currency",
                              currency: "ZAR",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(claim?.financials.surcharges.save)}
                          </Col>
                        </Row>
                      )}

                      {(claim?.patientClaimFinancials
                        ?.claimFinancialDescription ||
                        claim?.patientClaimFinancials
                          ?.claimFinancialAmount) && (
                        <>
                          <Row className="gx-0 mt-3 mb-1 ms-1 border-bottom border-primary">
                            <Col xs={6} className="fw-bold text-primary">
                              Patient Financials:
                            </Col>
                          </Row>
                          {claim?.patientClaimFinancials
                            .claimFinancialDescription && (
                            <Row className="gx-0 mb-1">
                              <Col xs={6} className="fw-normal text-primary">
                                Description:
                              </Col>
                              <Col xs={6} className="text-end">
                                {
                                  claim?.patientClaimFinancials
                                    ?.claimFinancialDescription
                                }
                              </Col>
                            </Row>
                          )}
                          {claim?.patientClaimFinancials
                            .claimFinancialAmount && (
                            <Row className="gx-0 mb-1 border-bottom border-primary border-1">
                              <Col xs={6} className="fw-normal text-primary">
                                Amount:
                              </Col>
                              <Col xs={6} className="text-end ">
                                {new Intl.NumberFormat("en-ZA", {
                                  style: "currency",
                                  currency: "ZAR",
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }).format(
                                  claim?.patientClaimFinancials
                                    ?.claimFinancialAmount
                                )}
                              </Col>
                            </Row>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </Collapse>
            </Container>
            )}
            {(invoice?.status === "Paid") && (
          
          <Button
              variant="outline-primary"
              onClick={handleUpdateInvoice}
              className="w-50 mt-2"
            >
              <i className="fa fa-edit me-2"></i>
              Update Invoice
            </Button>
        )}
            {invoice?.status !== "Paid" &&
              STATUS_BUTTONS[claim?.status]?.length > 0 && (
                <>
                  {buttonRows.map((row, rowIndex) => (
                    <Row className="mt-2 mb-2" key={rowIndex}>
                      <Col md={6}>
                        {row[0] && (
                          <Button
                            variant="primary"
                            onClick={row[0].onClick}
                            className="w-100"
                          >
                            <i className={`fa ${row[0].icon} me-2`}></i>
                            {row[0].label}
                          </Button>
                        )}
                      </Col>
                      <Col md={6}>
                        {row[1] && (
                          <Button
                            variant="secondary"
                            onClick={row[1].onClick}
                            className="w-100"
                          >
                            <i className={`fa ${row[1].icon} me-2`}></i>
                            {row[1].label}
                          </Button>
                        )}
                      </Col>
                    </Row>
                  ))}
                </>
              )}
            {/* Conditional rendering for "Update Member" button */}
            {claim?.status === "Switch Rejected" && (
              <Row className="mt-2">
                <Col md={6} className="d-flex justify-content-start">
                  {/* <EditPatient patientId={claim?.patientId} /> */}
                </Col>
              </Row>
            )}
          </Col>
        ) : (
          <Col className="d-flex flex-column align-items-start justify-content-top mt-2 gap-2">
            <Button variant="primary" onClick={handleReceipt} className="w-50">
              <i className="fa fa-receipt me-2"></i>
              Receive Payment
            </Button>

            <Button
              variant="outline-primary"
              onClick={handleUpdateInvoice}
              className="w-50 mt-2"
            >
              <i className="fa fa-edit me-2"></i>
              Update Invoice
            </Button>

            
          </Col>
        )}
        
      </Row>

      {showReceiptModal && account && (
        <CreateReceiptModal
          show={showReceiptModal}
          onHide={() => setShowReceiptModal(false)}
          account={account}
        />
      )}
      {showEmailModal && statementBlob && (
        <EmailInvoiceModal
          show={showEmailModal}
          onHide={() => setShowEmailModal(false)}
          defaultEmail={account.accountEmail}
          onSend={async (recipientEmail, chosenType = "invoice") => {
            try {
              const emailData = buildEmailData({ invoice, account, practice });

              const result = await sendInvoiceByEmail({
                email: recipientEmail,
                pdfBlob: statementBlob, // from ExportPDFInvoice
                type: chosenType, // keep "invoice" for now; modal can later allow "receipt"/"creditNote"
                accountId: account?._id,
                emailData, // 👈 NEW: pass variables for templating
              });

              if (result.error) throw new Error(result.error);
            } catch (err) {
              console.error("⛔ Failed to send email:", err);
              throw err; // so modal shows the error
            }
          }}
        />
      )}
    </div>
  );
}
